import React from 'react';
import {
  Modal, ModalBody, ModalFooter
} from 'reactstrap';
import { DATE_FORMAT, SelectedOption, StatusType } from '../../api/api.types';
import {
  DayOfWeek,
  DaysClosed,
  daysOpenOptions,
  DeskOpeningModel,
  DeskOpeningRequest,
  DeskOpeningType,
  OpenHoursModel,
  OutOfHoursType,
  OutOfHoursTypeOptions
} from '../../api/core/core.types';
import Select from '../forms/Select';
import TimeSelect from '../forms/TimeSelect';
import DatePicker, {IntervalDatePickerEvent} from '../forms/DatePicker';
import dayjs, {Dayjs} from 'dayjs';

interface DeskOpeningEditModalProps {
  toggle: () => void;
  isOpen: boolean;
  deskOpening?: DeskOpeningModel;
  action: DeskOpeningEditActions;
  onModalSubmit: (request: DeskOpeningRequest, action: DeskOpeningEditActions) => void;
}

interface DeskOpeningEditModalState {
  openMon: { value: string; label: string };
  openTue: { value: string; label: string };
  openWed: { value: string; label: string };
  openThu: { value: string; label: string };
  openFri: { value: string; label: string };
  openSat: { value: string; label: string };
  openSun: { value: string; label: string };
  validFrom: { value: string; isInvalid?: boolean };
  validTo: { value: string; isInvalid?: boolean };
  pickupOutOfHoursType: OutOfHoursType;
  returnOutOfHoursType: OutOfHoursType;
  openHours: {
    openHoursMon: { startTime: string; endTime: string; index: number }[];
    openHoursTue: { startTime: string; endTime: string; index: number }[];
    openHoursWed: { startTime: string; endTime: string; index: number }[];
    openHoursThu: { startTime: string; endTime: string; index: number }[];
    openHoursFri: { startTime: string; endTime: string; index: number }[];
    openHoursSat: { startTime: string; endTime: string; index: number }[];
    openHoursSun: { startTime: string; endTime: string; index: number }[];
  };
}

export enum DeskOpeningEditActions {
  ADD_NEW = 'ADD_NEW',
  EDIT = 'EDIT',
  DUPLICATE = 'DUPLICATE'
}

export default class DeskOpeningEditModal extends React.PureComponent<DeskOpeningEditModalProps, DeskOpeningEditModalState> {

  readonly state = {
    openMon: daysOpenOptions[1],
    openTue: daysOpenOptions[1],
    openWed: daysOpenOptions[1],
    openThu: daysOpenOptions[1],
    openFri: daysOpenOptions[1],
    openSat: daysOpenOptions[1],
    openSun: daysOpenOptions[1],
    validFrom: { value: dayjs().format(DATE_FORMAT), isInvalid: false },
    validTo: { value: dayjs().add(10, 'y').format(DATE_FORMAT), isInvalid: false },
    pickupOutOfHoursType: OutOfHoursType.NOT_ALLOWED,
    returnOutOfHoursType: OutOfHoursType.NOT_ALLOWED,
    openHours: {
      openHoursMon: [] as { startTime: string; endTime: string; index: number }[],
      openHoursTue: [] as { startTime: string; endTime: string; index: number }[],
      openHoursWed: [] as { startTime: string; endTime: string; index: number }[],
      openHoursThu: [] as { startTime: string; endTime: string; index: number }[],
      openHoursFri: [] as { startTime: string; endTime: string; index: number }[],
      openHoursSat: [] as { startTime: string; endTime: string; index: number }[],
      openHoursSun: [] as { startTime: string; endTime: string; index: number }[],
    }
  };

  componentDidMount() {
    this.createOpenHours();
  }

  async componentDidUpdate(prevProps: DeskOpeningEditModalProps) {
    if (prevProps !== this.props && this.props.isOpen) {
      if (this.props.deskOpening) {
        this.updateValues();
        await this.assignOpenHours(DayOfWeek.MONDAY, 'Mon');
        await this.assignOpenHours(DayOfWeek.TUESDAY, 'Tue');
        await this.assignOpenHours(DayOfWeek.WEDNESDAY, 'Wed');
        await this.assignOpenHours(DayOfWeek.THURSDAY, 'Thu');
        await this.assignOpenHours(DayOfWeek.FRIDAY, 'Fri');
        await this.assignOpenHours(DayOfWeek.SATURDAY, 'Sat');
        await this.assignOpenHours(DayOfWeek.SUNDAY, 'Sun');
        this.updateIsOpenValues();
      } else if (this.props.action === DeskOpeningEditActions.ADD_NEW) {
        this.reset();
      }
    }
  }

  updateValues = () => {
    if (this.props.deskOpening) {
      this.setState({
        validFrom: { value: this.props.deskOpening.validFrom },
        validTo: { value: this.props.deskOpening.validTo },
        pickupOutOfHoursType: this.props.deskOpening.pickupOutOfHoursType,
        returnOutOfHoursType: this.props.deskOpening.returnOutOfHoursType
      });
    }
  };

  onCancel = () => {
    this.reset();
    this.props.toggle();
  };

  reset = () => {
    this.setState({
      openMon: daysOpenOptions[1],
      openTue: daysOpenOptions[1],
      openWed: daysOpenOptions[1],
      openThu: daysOpenOptions[1],
      openFri: daysOpenOptions[1],
      openSat: daysOpenOptions[1],
      openSun: daysOpenOptions[1],
      validFrom: { value: dayjs().format(DATE_FORMAT), isInvalid: false },
      validTo: { value: dayjs().add(10, 'y').format(DATE_FORMAT), isInvalid: false },
      pickupOutOfHoursType: OutOfHoursType.NOT_ALLOWED,
      returnOutOfHoursType: OutOfHoursType.NOT_ALLOWED
    });

    this.createOpenHours();
  };

  updateIsOpenValues = () => {
    const deskOpening = this.props.deskOpening;
    let openMon = daysOpenOptions[2];
    let openTue = daysOpenOptions[2];
    let openWed = daysOpenOptions[2];
    let openThu = daysOpenOptions[2];
    let openFri = daysOpenOptions[2];
    let openSat = daysOpenOptions[2];
    let openSun = daysOpenOptions[2];

    if (deskOpening) {
      if (deskOpening.daysClosed.isMonClosed) openMon = daysOpenOptions[0];
      else if (deskOpening.openHours.filter((o) => o.dayOfWeek === DayOfWeek.MONDAY).length) openMon = daysOpenOptions[1];

      if (deskOpening.daysClosed.isTueClosed) openTue = daysOpenOptions[0];
      else if (deskOpening.openHours.filter((o) => o.dayOfWeek === DayOfWeek.TUESDAY).length) openTue = daysOpenOptions[1];

      if (deskOpening.daysClosed.isWedClosed) openWed = daysOpenOptions[0];
      else if (deskOpening.openHours.filter((o) => o.dayOfWeek === DayOfWeek.WEDNESDAY).length) openWed = daysOpenOptions[1];

      if (deskOpening.daysClosed.isThuClosed) openThu = daysOpenOptions[0];
      else if (deskOpening.openHours.filter((o) => o.dayOfWeek === DayOfWeek.THURSDAY).length) openThu = daysOpenOptions[1];

      if (deskOpening.daysClosed.isFriClosed) openFri = daysOpenOptions[0];
      else if (deskOpening.openHours.filter((o) => o.dayOfWeek === DayOfWeek.FRIDAY).length) openFri = daysOpenOptions[1];

      if (deskOpening.daysClosed.isSatClosed) openSat = daysOpenOptions[0];
      else if (deskOpening.openHours.filter((o) => o.dayOfWeek === DayOfWeek.SATURDAY).length) openSat = daysOpenOptions[1];

      if (deskOpening.daysClosed.isSunClosed) openSun = daysOpenOptions[0];
      else if (deskOpening.openHours.filter((o) => o.dayOfWeek === DayOfWeek.SUNDAY).length) openSun = daysOpenOptions[1];

      this.setState({
        openMon,
        openTue,
        openWed,
        openThu,
        openFri,
        openSat,
        openSun
      });
    }
  };

  createOpenHours = () => {
    const monday = [];
    const tuesday = [];
    const wednesday = [];
    const thursday = [];
    const friday = [];
    const saturday = [];
    const sunday = [];

    const defaultOpeningTime = {
      startTime: '700',
      endTime: '1800',
      index: 0
    };

    monday.push(defaultOpeningTime);
    tuesday.push(defaultOpeningTime);
    wednesday.push(defaultOpeningTime);
    thursday.push(defaultOpeningTime);
    friday.push(defaultOpeningTime);
    saturday.push(defaultOpeningTime);
    sunday.push(defaultOpeningTime);

    this.setState(
      {
        openHours: {
          openHoursMon: monday,
          openHoursTue: tuesday,
          openHoursWed: wednesday,
          openHoursThu: thursday,
          openHoursFri: friday,
          openHoursSat: saturday,
          openHoursSun: sunday,
        }
      }
    );
  };

  assignOpenHours = (day: string, field: string) => {
    if (this.props.deskOpening) {
      const openHours: { startTime: string; endTime: string; index: number}[] = [];

      this.props.deskOpening.openHours.filter((o) => o.dayOfWeek === day).forEach((oh: OpenHoursModel, index) => {
        openHours.push({
          startTime: String(oh.startTime),
          endTime: String(oh.endTime),
          index
        });
      });

      this.saveOpenHoursToState(field, openHours);
    }
  };

  onAddHoursClick = (field: string, openHoursArray: any[]) => {
    openHoursArray.push({ startTime: '700', endTime: '1800', index: openHoursArray.length });

    this.saveOpenHoursToState(field, openHoursArray);
  };

  removeOpenHours = async (field: string, openHoursArray: any[], position: number) => {
    const openings = openHoursArray.filter((oa) => oa.index !== position);

    this.saveOpenHoursToState(field, openings);
  };

  saveOpenHoursToState = (field: string, openHours: any[]) => {
    this.setState({
      openHours: {
        ...this.state.openHours,
        [`openHours${field}`]: openHours
      }
    });
  };

  onSubmit = () => {
    if (this.isFormInvalid()) return;

    const daysClosed: DaysClosed = {
      isMonClosed: this.state.openMon === daysOpenOptions[0],
      isTueClosed: this.state.openTue === daysOpenOptions[0],
      isWedClosed: this.state.openWed === daysOpenOptions[0],
      isThuClosed: this.state.openThu === daysOpenOptions[0],
      isFriClosed: this.state.openFri === daysOpenOptions[0],
      isSatClosed: this.state.openSat === daysOpenOptions[0],
      isSunClosed: this.state.openSun === daysOpenOptions[0],
    };

    const request: DeskOpeningRequest = {
      validFrom: this.state.validFrom.value,
      validTo: this.state.validTo.value,
      statusType: StatusType.ACTIVE,
      pickupOutOfHoursType: this.state.pickupOutOfHoursType,
      returnOutOfHoursType: this.state.returnOutOfHoursType,
      daysClosed,
      openHours: this.getOpenHours(),
      deskOpeningType: DeskOpeningType.STANDARD
    };

    this.props.onModalSubmit(request, this.props.deskOpening ? DeskOpeningEditActions.EDIT : DeskOpeningEditActions.ADD_NEW);
    this.reset();
  };

  onAddToAllClick = (openValue: { value: string; label: string}, openHours: { startTime: string; endTime: string; index: number; isInvalid?: boolean }[]) => {
    const openHoursToApply = JSON.parse(JSON.stringify(openHours));

    this.setState({
      openMon: openValue,
      openTue: openValue,
      openWed: openValue,
      openThu: openValue,
      openFri: openValue,
      openSat: openValue,
      openSun: openValue,
      openHours: {
        openHoursMon: JSON.parse(JSON.stringify(openHoursToApply)),
        openHoursTue: JSON.parse(JSON.stringify(openHoursToApply)),
        openHoursWed: JSON.parse(JSON.stringify(openHoursToApply)),
        openHoursThu: JSON.parse(JSON.stringify(openHoursToApply)),
        openHoursFri: JSON.parse(JSON.stringify(openHoursToApply)),
        openHoursSat: JSON.parse(JSON.stringify(openHoursToApply)),
        openHoursSun: JSON.parse(JSON.stringify(openHoursToApply)),
      }
    });
  };

  isFormInvalid = () => {
    if (this.state.validFrom.isInvalid || this.state.validTo.isInvalid) return true;

    return false;
  };

  getOpenHours = () => {
    const openHours: OpenHoursModel[] = [];

    if (this.state.openMon === daysOpenOptions[1]) {
      this.state.openHours.openHoursMon.forEach((v: any) => {
        openHours.push({ startTime: v.startTime, endTime: v.endTime, dayOfWeek: DayOfWeek.MONDAY });
      });
    }

    if (this.state.openTue === daysOpenOptions[1]) {
      this.state.openHours.openHoursTue.forEach((v: any) => {
        openHours.push({ startTime: v.startTime, endTime: v.endTime, dayOfWeek: DayOfWeek.TUESDAY });
      });
    }

    if (this.state.openWed === daysOpenOptions[1]) {
      this.state.openHours.openHoursWed.forEach((v: any) => {
        openHours.push({ startTime: v.startTime, endTime: v.endTime, dayOfWeek: DayOfWeek.WEDNESDAY });
      });
    }

    if (this.state.openThu === daysOpenOptions[1]) {
      this.state.openHours.openHoursThu.forEach((v: any) => {
        openHours.push({ startTime: v.startTime, endTime: v.endTime, dayOfWeek: DayOfWeek.THURSDAY });
      });
    }

    if (this.state.openFri === daysOpenOptions[1]) {
      this.state.openHours.openHoursFri.forEach((v: any) => {
        openHours.push({ startTime: v.startTime, endTime: v.endTime, dayOfWeek: DayOfWeek.FRIDAY });
      });
    }

    if (this.state.openSat === daysOpenOptions[1]) {
      this.state.openHours.openHoursSat.forEach((v: any) => {
        openHours.push({ startTime: v.startTime, endTime: v.endTime, dayOfWeek: DayOfWeek.SATURDAY });
      });
    }

    if (this.state.openSun === daysOpenOptions[1]) {
      this.state.openHours.openHoursSun.forEach((v: any) => {
        openHours.push({ startTime: v.startTime, endTime: v.endTime, dayOfWeek: DayOfWeek.SUNDAY });
      });
    }

    return openHours;
  };

  onDateChange = (field: IntervalDatePickerEvent, date?: Dayjs) => {
    let isInvalid;
    if (!date) {
      isInvalid = true;
    } else if (field === IntervalDatePickerEvent.FROM) {
      isInvalid = dayjs(this.state.validTo.value).isBefore(date);
    } else {
      isInvalid = date.isBefore(dayjs(this.state.validFrom.value));
    }

    this.setState({
      ...this.state,
      [`valid${field.valueOf()}`]: { value: date ? date.format(DATE_FORMAT) : undefined, isInvalid },
    });
  };

  patchField = (option: SelectedOption, field: string) => {
    this.setState({ ...this.state, [field]: option.value });
  };

  onTimeChange = async (value: { startTime?: string; endTime?: string; index: number }, day: string, openings: any[]) => {
    const openHours = JSON.parse(JSON.stringify(openings));
    if (value.startTime) {
      openHours[value.index].startTime = value.startTime;
    } else {
      openHours[value.index].endTime = value.endTime;
    }

    await this.saveOpenHoursToState(day, openHours);
  };

  render() {
    return (
      <Modal style={{ minWidth: '1314px' }} isOpen={this.props.isOpen} toggle={this.props.toggle} autoFocus={false}>
        <ModalBody toggle={this.props.toggle}>
          <div className="desk-opening-modal">
            <div>
              <div className="mb-2 d-flex justify-content-start">
                {this.props.deskOpening && (
                  <h1>Edit period</h1>
                )}
                {!this.props.deskOpening && (
                <h1>Add new period</h1>
                )}
              </div>
            </div>
            <div>
              <div className="ml-1 d-flex row align-items-center justify-content-start">
                <div className="mr-2 text-light">Period starting date:</div>
                <div className="modal-date-picker">
                  <DatePicker
                      onChange={(val) => this.onDateChange(IntervalDatePickerEvent.FROM, val)}
                      isEdit
                      date={this.state.validFrom.value ? dayjs(this.state.validFrom.value) : undefined}
                      isInvalid={this.state.validFrom.isInvalid}
                      id='0'
                  />
                </div>
                <div className="ml-1 mr-1"> - </div>
                <div className="modal-date-picker">
                  <DatePicker
                      onChange={(val) => this.onDateChange(IntervalDatePickerEvent.TO, val)}
                      isEdit
                      date={this.state.validTo.value ? dayjs(this.state.validTo.value) : undefined}
                      isInvalid={this.state.validTo.isInvalid}
                      id='1'
                  />
                </div>
              </div>
              <div className="d-flex mt-3">
                <div className="d-flex flex-column align-items-center">
                  <div className="edit-open-hours-container mr-1 mb-1">
                    <div className="d-flex justify-content-center mb-1">
                      <label className="text-bold">MONDAY</label>
                    </div>
                    <Select
                      value={this.state.openMon}
                      className="mb-1"
                      placeholder="Choose opening type"
                      disableClear
                      options={daysOpenOptions}
                      closeMenuOnSelect={false}
                      hideSelectedOptions={false}
                      label="openMon"
                      onChange={(value: any) => { this.setState({ openMon: value }); }}
                    />
                    {this.state.openMon === daysOpenOptions[1] && (
                    <>
                      { this.state.openHours.openHoursMon.map((o) => (
                        <div className="d-flex row-justify-content-start align-items-center" key={`Mon${o.index}`}>
                          <button type="button" className="btn-add-remove" onClick={() => this.removeOpenHours('Mon', this.state.openHours.openHoursMon, o.index)}>-</button>
                          <TimeSelect onChange={(value) => this.onTimeChange({ startTime: value, index: o.index }, 'Mon', this.state.openHours.openHoursMon)} value={o.startTime} />
                          <div className="margin-left-minimal margin-right-minimal"> - </div>
                          <TimeSelect onChange={(value) => this.onTimeChange({ endTime: value, index: o.index }, 'Mon', this.state.openHours.openHoursMon)} value={o.endTime} />
                        </div>
                      ))}
                      <button type="button" className="btn-add-remove mt-1" onClick={() => this.onAddHoursClick('Mon', this.state.openHours.openHoursMon)}>+</button>
                    </>
                    )}
                  </div>
                  <button type="button" onClick={() => this.onAddToAllClick(this.state.openMon, this.state.openHours.openHoursMon)} className="btn btn-text-only btn-text-only-gray">Apply to all days</button>
                </div>
                <div className="d-flex flex-column align-items-center">
                  <div className="edit-open-hours-container mr-1 mb-1">
                    <div className="d-flex justify-content-center mb-1">
                      <label className="text-bold">TUESDAY</label>
                    </div>
                    <Select
                      value={this.state.openTue}
                      className="mb-1"
                      placeholder="Choose opening type"
                      disableClear
                      options={daysOpenOptions}
                      closeMenuOnSelect={false}
                      hideSelectedOptions={false}
                      label="openTue"
                      onChange={(value: any) => { this.setState({ openTue: value }); }}
                    />
                    {this.state.openTue === daysOpenOptions[1] && (
                    <>
                      { this.state.openHours.openHoursTue.map((o) => (
                        <div className="d-flex row-justify-content-start align-items-center" key={`Tue${o.index}`}>
                          <button type="button" className="btn-add-remove" onClick={() => this.removeOpenHours('Tue', this.state.openHours.openHoursTue, o.index)}>-</button>
                          <TimeSelect onChange={(value) => this.onTimeChange({ startTime: value, index: o.index }, 'Tue', this.state.openHours.openHoursTue)} value={o.startTime} />
                          <div className="margin-left-minimal margin-right-minimal"> - </div>
                          <TimeSelect onChange={(value) => this.onTimeChange({ endTime: value, index: o.index }, 'Tue', this.state.openHours.openHoursTue)} value={o.endTime} />
                        </div>
                      ))}
                      <button type="button" className="btn-add-remove mt-1" onClick={() => this.onAddHoursClick('Tue', this.state.openHours.openHoursTue)}>+</button>
                    </>
                    )}
                  </div>
                  <button type="button" onClick={() => this.onAddToAllClick(this.state.openTue, this.state.openHours.openHoursTue)} className="btn btn-text-only btn-text-only-gray">Apply to all days</button>
                </div>
                <div className="d-flex flex-column align-items-center">
                  <div className="edit-open-hours-container mr-1 mb-1">
                    <div className="d-flex justify-content-center mb-1">
                      <label className="text-bold">WEDNESDAY</label>
                    </div>
                    <Select
                      value={this.state.openWed}
                      className="mb-1"
                      placeholder="Choose opening type"
                      disableClear
                      options={daysOpenOptions}
                      closeMenuOnSelect={false}
                      hideSelectedOptions={false}
                      label="openWed"
                      onChange={(value: any) => { this.setState({ openWed: value }); }}
                    />
                    {this.state.openWed === daysOpenOptions[1] && (
                      <>
                        { this.state.openHours.openHoursWed.map((o) => (
                          <div className="d-flex row-justify-content-start align-items-center" key={`Wed${o.index}`}>
                            <button type="button" className="btn-add-remove" onClick={() => this.removeOpenHours('Wed', this.state.openHours.openHoursWed, o.index)}>-</button>
                            <TimeSelect onChange={(value) => this.onTimeChange({ startTime: value, index: o.index }, 'Wed', this.state.openHours.openHoursWed)} value={o.startTime} />
                            <div className="margin-left-minimal margin-right-minimal"> - </div>
                            <TimeSelect onChange={(value) => this.onTimeChange({ endTime: value, index: o.index }, 'Wed', this.state.openHours.openHoursWed)} value={o.endTime} />
                          </div>
                        ))}
                        <button type="button" className="btn-add-remove mt-1" onClick={() => this.onAddHoursClick('Wed', this.state.openHours.openHoursWed)}>+</button>
                      </>
                    )}
                  </div>
                  <button type="button" onClick={() => this.onAddToAllClick(this.state.openWed, this.state.openHours.openHoursWed)} className="btn btn-text-only btn-text-only-gray">Apply to all days</button>
                </div>
                <div className="d-flex flex-column align-items-center">
                  <div className="edit-open-hours-container mb-1 mr-1">
                    <div className="d-flex justify-content-center mb-1">
                      <label className="text-bold">THURSDAY</label>
                    </div>
                    <Select
                      value={this.state.openThu}
                      className="mb-1"
                      placeholder="Choose opening type"
                      disableClear
                      options={daysOpenOptions}
                      closeMenuOnSelect={false}
                      hideSelectedOptions={false}
                      label="openThu"
                      onChange={(value: any) => { this.setState({ openThu: value }); }}
                    />
                    {this.state.openThu === daysOpenOptions[1] && (
                      <>
                        { this.state.openHours.openHoursThu.map((o) => (
                          <div className="d-flex row-justify-content-start align-items-center" key={`Thu${o.index}`}>
                            <button type="button" className="btn-add-remove" onClick={() => this.removeOpenHours('Thu', this.state.openHours.openHoursThu, o.index)}>-</button>
                            <TimeSelect onChange={(value) => this.onTimeChange({ startTime: value, index: o.index }, 'Thu', this.state.openHours.openHoursThu)} value={o.startTime} />
                            <div className="margin-left-minimal margin-right-minimal"> - </div>
                            <TimeSelect onChange={(value) => this.onTimeChange({ endTime: value, index: o.index }, 'Thu', this.state.openHours.openHoursThu)} value={o.endTime} />
                          </div>
                        ))}
                        <button type="button" className="btn-add-remove mt-1" onClick={() => this.onAddHoursClick('Thu', this.state.openHours.openHoursThu)}>+</button>
                      </>
                    )}
                  </div>
                  <button type="button" onClick={() => this.onAddToAllClick(this.state.openThu, this.state.openHours.openHoursThu)} className="btn btn-text-only btn-text-only-gray">Apply to all days</button>
                </div>
                <div className="d-flex flex-column align-items-center">
                  <div className="edit-open-hours-container mb-1 mr-1">
                    <div className="d-flex justify-content-center mb-1">
                      <label className="text-bold">FRIDAY</label>
                    </div>
                    <Select
                      value={this.state.openFri}
                      className="mb-1"
                      placeholder="Choose opening type"
                      disableClear
                      options={daysOpenOptions}
                      closeMenuOnSelect={false}
                      hideSelectedOptions={false}
                      label="openFri"
                      onChange={(value: any) => { this.setState({ openFri: value }); }}
                    />
                    {this.state.openFri === daysOpenOptions[1] && (
                      <>
                        { this.state.openHours.openHoursFri.map((o) => (
                          <div className="d-flex row-justify-content-start align-items-center" key={`Fi${o.index}`}>
                            <button type="button" className="btn-add-remove" onClick={() => this.removeOpenHours('Fri', this.state.openHours.openHoursFri, o.index)}>-</button>
                            <TimeSelect onChange={(value) => this.onTimeChange({ startTime: value, index: o.index }, 'Fri', this.state.openHours.openHoursFri)} value={o.startTime} />
                            <div className="margin-left-minimal margin-right-minimal"> - </div>
                            <TimeSelect onChange={(value) => this.onTimeChange({ endTime: value, index: o.index }, 'Fri', this.state.openHours.openHoursFri)} value={o.endTime} />
                          </div>
                        ))}
                        <button type="button" className="btn-add-remove mt-1" onClick={() => this.onAddHoursClick('Fri', this.state.openHours.openHoursFri)}>+</button>
                      </>
                    )}
                  </div>
                  <button type="button" onClick={() => this.onAddToAllClick(this.state.openFri, this.state.openHours.openHoursFri)} className="btn btn-text-only btn-text-only-gray">Apply to all days</button>
                </div>
                <div className="d-flex flex-column align-items-center">
                  <div className="edit-open-hours-container mr-1 mb-1">
                    <div className="d-flex justify-content-center mb-1">
                      <label className="text-bold">SATURDAY</label>
                    </div>
                    <Select
                      value={this.state.openSat}
                      className="mb-1"
                      disableClear
                      placeholder="Choose opening type"
                      options={daysOpenOptions}
                      closeMenuOnSelect={false}
                      hideSelectedOptions={false}
                      label="openSat"
                      onChange={(value: any) => { this.setState({ openSat: value }); }}
                    />
                    {this.state.openSat === daysOpenOptions[1] && (
                      <>
                        { this.state.openHours.openHoursSat.map((o) => (
                          <div className="d-flex row-justify-content-start align-items-center" key={`Sat${o.index}`}>
                            <button type="button" className="btn-add-remove" onClick={() => this.removeOpenHours('Sat', this.state.openHours.openHoursSat, o.index)}>-</button>
                            <TimeSelect onChange={(value) => this.onTimeChange({ startTime: value, index: o.index }, 'Sat', this.state.openHours.openHoursSat)} value={o.startTime} />
                            <div className="margin-left-minimal margin-right-minimal"> - </div>
                            <TimeSelect onChange={(value) => this.onTimeChange({ endTime: value, index: o.index }, 'Sat', this.state.openHours.openHoursSat)} value={o.endTime} />
                          </div>
                        ))}
                        <button type="button" className="btn-add-remove mt-1" onClick={() => this.onAddHoursClick('Sat', this.state.openHours.openHoursSat)}>+</button>
                      </>
                    )}
                  </div>
                  <button type="button" onClick={() => this.onAddToAllClick(this.state.openSat, this.state.openHours.openHoursSat)} className="btn btn-text-only btn-text-only-gray">Apply to all days</button>
                </div>
                <div className="d-flex flex-column align-items-center">
                  <div className="edit-open-hours-container mb-1 mr-1">
                    <div className="d-flex justify-content-center mb-1">
                      <label className="text-bold">SUNDAY</label>
                    </div>
                    <Select
                      value={this.state.openSun}
                      className="mb-1"
                      disableClear
                      placeholder="Choose opening type"
                      options={daysOpenOptions}
                      closeMenuOnSelect={false}
                      hideSelectedOptions={false}
                      label="openSat"
                      onChange={(value: any) => { this.setState({ openSun: value }); }}
                    />
                    {this.state.openSun === daysOpenOptions[1] && (
                      <>
                        { this.state.openHours.openHoursSun.map((o) => (
                          <div className="d-flex row-justify-content-start align-items-center" key={`sun${o.index}`}>
                            <button type="button" className="btn-add-remove" onClick={() => this.removeOpenHours('Sun', this.state.openHours.openHoursSun, o.index)}>-</button>
                            <TimeSelect onChange={(value) => this.onTimeChange({ startTime: value, index: o.index }, 'Sun', this.state.openHours.openHoursSun)} value={o.startTime} />
                            <div className="margin-left-minimal margin-right-minimal"> - </div>
                            <TimeSelect onChange={(value) => this.onTimeChange({ endTime: value, index: o.index }, 'Sun', this.state.openHours.openHoursSun)} value={o.endTime} />
                          </div>
                        ))}
                        <button type="button" className="btn-add-remove mt-1" onClick={() => this.onAddHoursClick('Sun', this.state.openHours.openHoursSun)}>+</button>
                      </>
                    )}
                  </div>
                  <button type="button" onClick={() => this.onAddToAllClick(this.state.openSun, this.state.openHours.openHoursSun)} className="btn btn-text-only btn-text-only-gray">Apply to all days</button>
                </div>
              </div>
              <div className="d-flex row info-container align-items-center mt-2 mb-1">
                <label className="ml-2 mr-1 align-items-center">Out of hours pickup: </label>
                <Select
                  value={OutOfHoursTypeOptions.find((o) => o.value === this.state.pickupOutOfHoursType)}
                  className="mr-1"
                  placeholder="Out of hours pickup"
                  options={OutOfHoursTypeOptions}
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  label="pickupOutOfHoursType"
                  onChange={(value) => this.patchField(value as SelectedOption, 'pickupOutOfHoursType')}
                />
              </div>
              <div className="d-flex row info-container align-items-center">
                <label className="ml-2 d-flex">Out of hours drop-off: </label>
                <Select
                  value={OutOfHoursTypeOptions.find((o) => o.value === this.state.returnOutOfHoursType)}
                  className="mr-1"
                  placeholder="Out of hours return"
                  options={OutOfHoursTypeOptions}
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  label="returnOutOfHoursType"
                  onChange={(value) => this.patchField(value as SelectedOption, 'returnOutOfHoursType')}
                />
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <div>
            <button type="button" className="btn btn-outline-secondary mr-1" onClick={() => { this.onCancel(); }}>CANCEL</button>
            <button type="button" className="btn btn-primary" onClick={this.onSubmit}>SAVE</button>
          </div>
        </ModalFooter>
      </Modal>
    );
  }
}
